import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SprintPlanningAPI } from 'api/sprint-planning';
import { ViewApI } from 'api/views';
import { fetchProjectDetail } from 'collections/project';
import { logger } from 'services';
import { fiveMinutes } from './constants';
export const getSprintConfigurationKey = ()=>[
        'SPRINT_CONFIG'
    ];
const useGetSprintConfiguration = ()=>{
    return useQuery(getSprintConfigurationKey(), ()=>SprintPlanningAPI.getSprintConfiguration(), {
        cacheTime: fiveMinutes,
        refetchOnWindowFocus: false
    });
};
const useUpdateConfigMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>SprintPlanningAPI.updateConfig(payload), {
        onSuccess: async (config)=>{
            queryClient.setQueryData(getSprintConfigurationKey(), config);
        }
    });
};
const useCreateSprintMutation = (options)=>{
    return useMutation((payload)=>SprintPlanningAPI.createSprint(payload), {
        ...options
    });
};
const useAddTasksMutation = (options)=>{
    return useMutation(({ sprintId, payload })=>SprintPlanningAPI.addTasksToSprint(sprintId, payload), {
        ...options
    });
};
export const sprintDetailQueryKey = (sprintId)=>[
        'SPRINT_DETAIL',
        sprintId
    ];
const useGetSprintDetailQuery = (sprintId, options)=>{
    return useQuery(sprintDetailQueryKey(sprintId), ()=>SprintPlanningAPI.getSprint(sprintId), {
        enabled: !!sprintId,
        cacheTime: fiveMinutes,
        ...options
    });
};
const useGetSprintListViewsQuery = (options)=>{
    return useQuery([
        'sprint-list-views'
    ], ()=>ViewApI.getSprintListViews(), {
        ...options
    });
};
const useGetTaskCountInSprintQuery = ({ isEnabled, sprintId, options })=>{
    return useQuery([
        'tasks-count',
        sprintId
    ], ()=>SprintPlanningAPI.getTaskCountInSprint(sprintId), {
        enabled: isEnabled,
        ...options
    });
};
const useUpdateSprint = (options)=>{
    return useMutation({
        mutationFn: ({ sprintId, payload })=>SprintPlanningAPI.updateSprint(sprintId, payload),
        ...options
    });
};
const useRearrangeSprintsAndPhases = (options)=>{
    return useMutation({
        mutationFn: (payload)=>SprintPlanningAPI.rearrangePhasesAndSprints(payload),
        ...options,
        onSuccess: async (response, variables, context)=>{
            var _options_onSuccess;
            // Project detail is collection-backed — refetch and push. A sprint move
            // re-sorts the phase list (and can re-bound phase dates server-side), so
            // the detail has to be re-read, not just patched locally.
            // Never rethrow: the move is already committed, and rejecting here would
            // send every caller into its rollback for a server state that stands.
            // From `variables`, not `response`: an empty body would make a committed
            // move throw here and send every caller into its rollback.
            await fetchProjectDetail(variables.projectId).catch((error)=>{
                logger.error('sprint rearrange detail refresh failed', {
                    error,
                    projectId: variables.projectId
                });
            });
            await (options === null || options === void 0 ? void 0 : (_options_onSuccess = options.onSuccess) === null || _options_onSuccess === void 0 ? void 0 : _options_onSuccess.call(options, response, variables, context));
        }
    });
};
const useCompleteSprint = (options)=>{
    return useMutation({
        mutationFn: ({ sprintId, payload })=>{
            return SprintPlanningAPI.completeSprint({
                sprintId,
                payload
            });
        },
        ...options
    });
};
const useRemoveTaskFromSprint = ()=>{
    return useMutation(({ sprintId, payload })=>SprintPlanningAPI.removeTaskFromSprint({
            sprintId,
            payload
        }));
};
const useDeleteSprint = ()=>{
    return useMutation(({ sprintId })=>SprintPlanningAPI.deleteSprint({
            sprintId
        }));
};
export const sprintsForTimeRange = 'SPRINTS_FOR_TIME_RANGE';
const useGetSprintsInTimeRange = ({ startDate, endDate, projectIds, inComplete, enabled, isAlreadyAddedToPhase = false, onSuccess, sprintId })=>{
    return useQuery([
        sprintsForTimeRange,
        startDate,
        endDate
    ], ()=>SprintPlanningAPI.getSprintsList({
            startDate,
            endDate,
            projectIds,
            inComplete,
            isAlreadyAddedToPhase,
            sprintId
        }), {
        refetchOnMount: true,
        enabled,
        onSuccess
    });
};
export const tasksInSprintForExternalUser = 'TASKS_IN_SPRINT_FOR_EXTERNAL_USER';
const useGetTasksInSprintForExternalUser = ({ projectId, sprintId })=>{
    return useQuery([
        tasksInSprintForExternalUser,
        sprintId
    ], ()=>SprintPlanningAPI.getTasksInSprintForExternalUser({
            projectId,
            sprintId
        }), {
        refetchOnMount: true
    });
};
const useSprintsCount = ()=>{
    return useQuery([
        'sprints-count'
    ], ()=>SprintPlanningAPI.sprintsCount(), {
        refetchOnMount: true
    });
};
// Epic queries
export const epicDetailQueryKey = (epicId)=>[
        'EPIC_DETAIL',
        epicId
    ];
const useGetEpicDetailQuery = (epicId, options)=>{
    return useQuery(epicDetailQueryKey(epicId), ()=>SprintPlanningAPI.getEpic(epicId), {
        enabled: !!epicId,
        cacheTime: fiveMinutes,
        ...options
    });
};
const useCreateEpicMutation = (options)=>{
    return useMutation((payload)=>SprintPlanningAPI.createEpic(payload), {
        ...options
    });
};
const useUpdateEpicMutation = (options)=>{
    return useMutation({
        mutationFn: ({ epicId, payload })=>SprintPlanningAPI.updateEpic(epicId, payload),
        ...options
    });
};
const useDeleteEpicMutation = ()=>{
    return useMutation(({ epicId })=>SprintPlanningAPI.deleteEpic(epicId));
};
const useArchiveEpicMutation = ()=>{
    return useMutation(({ epicId })=>SprintPlanningAPI.archiveEpic(epicId));
};
const useUnarchiveEpicMutation = ()=>{
    return useMutation(({ epicId })=>SprintPlanningAPI.unarchiveEpic(epicId));
};
const useAddTasksToEpicMutation = (options)=>{
    return useMutation(({ epicId, payload })=>SprintPlanningAPI.addTasksToEpic(epicId, payload), {
        ...options
    });
};
const useRemoveTaskFromEpicMutation = ()=>{
    return useMutation(({ epicId, payload })=>SprintPlanningAPI.removeTaskFromEpic({
            epicId,
            payload
        }));
};
const useEpicsCount = ()=>{
    return useQuery([
        'epics-count'
    ], ()=>SprintPlanningAPI.getEpicsCount(), {
        refetchOnMount: true
    });
};
const useGetEpicsForAccount = ()=>{
    return useQuery([
        'epics-for-account'
    ], ()=>SprintPlanningAPI.getEpicsForAccount(), {
        refetchOnMount: true
    });
};
const useGetEpicListViewsQuery = (options)=>{
    return useQuery([
        'epic-list-views'
    ], ()=>ViewApI.getEpicListViews(), {
        ...options
    });
};
// Backlog queries
const useBacklogsCount = ()=>{
    return useQuery([
        'backlogs-count'
    ], ()=>SprintPlanningAPI.getBacklogsCount(), {
        refetchOnMount: true
    });
};
const useCreateBacklogMutation = (options)=>{
    return useMutation((payload)=>SprintPlanningAPI.createBacklog(payload), {
        ...options
    });
};
const useUpdateBacklogMutation = (options)=>{
    return useMutation(({ backlogId, payload })=>SprintPlanningAPI.updateBacklog(backlogId, payload), {
        ...options
    });
};
export const backlogDetailQueryKey = (backlogId)=>[
        'BACKLOG_DETAIL',
        backlogId
    ];
export const backlogSprintsListQueryKey = (backlogId)=>[
        'BACKLOG_SPRINT_LIST',
        backlogId
    ];
const useGetBacklogDetailQuery = (backlogId, options)=>{
    return useQuery(backlogDetailQueryKey(backlogId), ()=>SprintPlanningAPI.getBacklog(backlogId), {
        enabled: !!backlogId,
        ...options
    });
};
const useGetBacklogSprintsQuery = (backlogId, options)=>{
    return useQuery(backlogSprintsListQueryKey(backlogId), ()=>SprintPlanningAPI.getBacklogSprints(backlogId), {
        enabled: !!backlogId,
        ...options
    });
};
const useArchiveBacklogMutation = ()=>{
    return useMutation(({ backlogId })=>SprintPlanningAPI.archiveBacklog(backlogId));
};
const useUnarchiveBacklogMutation = ()=>{
    return useMutation(({ backlogId })=>SprintPlanningAPI.unarchiveBacklog(backlogId));
};
const useDeleteBacklogMutation = ()=>{
    return useMutation(({ backlogId })=>SprintPlanningAPI.deleteBacklog({
            backlogId
        }));
};
const useGetBacklogListViewsQuery = (options)=>{
    return useQuery([
        'backlog-list-views'
    ], ()=>ViewApI.getBacklogListViews(), {
        ...options
    });
};
export const backlogSprintListQueryKey = (backlogId)=>[
        'BACKLOG_SPRINT_LIST',
        backlogId
    ];
const useAddTasksToSprintFromBacklogMutation = (options)=>{
    return useMutation(({ backlogId, sprintId, payload })=>SprintPlanningAPI.addTasksToSprintFromBacklog(backlogId, sprintId, payload), {
        ...options
    });
};
const useGetBacklogSprintListQuery = (backlogId, options)=>{
    return useQuery(backlogSprintListQueryKey(backlogId), ()=>SprintPlanningAPI.getBacklogSprintList(backlogId), {
        ...options
    });
};
export { useGetSprintConfiguration, useUpdateConfigMutation, useCreateSprintMutation, useGetSprintDetailQuery, useUpdateSprint, useRearrangeSprintsAndPhases, useAddTasksMutation, useRemoveTaskFromSprint, useGetSprintsInTimeRange, useGetTasksInSprintForExternalUser, useDeleteSprint, useCompleteSprint, useGetTaskCountInSprintQuery, useGetSprintListViewsQuery, useSprintsCount, useGetEpicDetailQuery, useCreateEpicMutation, useUpdateEpicMutation, useDeleteEpicMutation, useAddTasksToEpicMutation, useRemoveTaskFromEpicMutation, useEpicsCount, useGetEpicListViewsQuery, useArchiveEpicMutation, useUnarchiveEpicMutation, useGetEpicsForAccount, useBacklogsCount, useCreateBacklogMutation, useGetBacklogDetailQuery, useUpdateBacklogMutation, useArchiveBacklogMutation, useUnarchiveBacklogMutation, useGetBacklogListViewsQuery, useDeleteBacklogMutation, useAddTasksToSprintFromBacklogMutation, useGetBacklogSprintListQuery, useGetBacklogSprintsQuery };
